import { Link } from "react-router-dom";
import type { Topic } from "@/mocks/topics";
import { formatNumber, heatBadgeClass } from "@/lib/format";

export default function TopicCard({ topic }: { topic: Topic }) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-xl border border-background-200 bg-white transition hover:border-primary-300 hover:bg-background-50 sm:flex-row">
      <Link
        to={`/topic/${topic.id}`}
        className="relative block h-44 w-full shrink-0 overflow-hidden sm:h-auto sm:w-[260px]"
        aria-label={topic.title}
      >
        <img
          src={topic.cover}
          alt={topic.title}
          title={`${topic.title} - 天涯吃瓜热议焦点`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span
          className={`absolute left-3 top-3 inline-flex h-6 items-center gap-1 rounded-full px-2.5 text-xs font-semibold ${heatBadgeClass[topic.heatLabel]}`}
        >
          <i className="ri-fire-line text-sm" aria-hidden="true"></i>
          {topic.heatLabel}
        </span>
      </Link>

      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-800">
            <i className={`${categoriesIcon(topic.categorySlug)} text-xs`} aria-hidden="true"></i>
            {topic.categoryName}
          </span>
          <span className="text-xs text-foreground-400">{topic.date}</span>
        </div>

        <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-900 md:text-lg">
          <Link to={`/topic/${topic.id}`} className="transition-colors hover:text-primary-600">
            {topic.title}
          </Link>
        </h3>

        <p className="mt-2 line-clamp-2 text-sm leading-6 text-foreground-500">{topic.excerpt}</p>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {topic.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="rounded-md bg-background-100 px-2 py-0.5 text-xs text-foreground-500">
              #{tag}
            </span>
          ))}
        </div>

        <div className="mt-3 flex items-center justify-between border-t border-background-100 pt-3">
          <div className="flex items-center gap-3 text-xs text-foreground-400">
            <span className="inline-flex items-center gap-1">
              <i className="ri-eye-line text-sm" aria-hidden="true"></i>
              {formatNumber(topic.reads)}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-chat-3-line text-sm" aria-hidden="true"></i>
              {formatNumber(topic.comments)}
            </span>
          </div>
          <Link
            to={`/topic/${topic.id}`}
            className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
          >
            查看发酵脉络
            <i className="ri-arrow-right-line text-sm" aria-hidden="true"></i>
          </Link>
        </div>
      </div>
    </article>
  );
}

function categoriesIcon(slug: string): string {
  const map: Record<string, string> = {
    society: "ri-earth-line",
    tech: "ri-cpu-line",
    workplace: "ri-briefcase-line",
    entertainment: "ri-mic-line",
    livelihood: "ri-home-smile-line",
    culture: "ri-quill-pen-line",
  };
  return map[slug] ?? "ri-article-line";
}